import { Component, computed, inject, input, output } from '@angular/core';
import { faTrashCan } from '@fortawesome/pro-regular-svg-icons';

import { ToOrdinalNumber, ToPlainText } from '@empathy/global/functions';
import { BooleanOperators, Command, Condition, ConditionSaved, QuestionType, RangeOperators, RankingTypes, RatingType } from '@empathy/survey/base';
import { OperatorText, Question } from '@empathy/survey-core';

import { SurveyEditorService } from '../../../services/survey-editor.service';

@Component({
  selector: 'empathy-conditions-saved',
  templateUrl: 'conditions-saved.component.html',
  styleUrls: ['./conditions-saved.component.scss', './_conditions-saved-theme.component.scss']
})
export class ConditionsSavedComponent {
  conditions = input<(Condition | Command)[]>();
  previousQuestions = input<Question[]>([]);

  removeCondition = output<number>();

  conditionsRealValue = computed(() => this.conditions().map((c) => this.mapConditionWithValues(c, this.previousQuestions())));

  surveyEditorService = inject(SurveyEditorService);

  booleanOperators = BooleanOperators;
  faTrashCan = faTrashCan;
  operators = OperatorText;
  rangeOperators = RangeOperators;

  private mapConditionWithValues(condition: Condition | Command, questions: Question[]): ConditionSaved | Command {
    if (condition === 'AND' || condition === 'OR') {
      return condition;
    }

    const question = questions.find((q) => q.id == condition.questionId);
    const conditionPreview = structuredClone(condition) as ConditionSaved;

    if (!question) {
      return conditionPreview;
    }

    const isRankOperator = [QuestionType.RankSort, QuestionType.Ranking].includes(question.type);

    let infoLabel = ToPlainText(question.title);

    conditionPreview.questionText = question.title;

    if (condition.cardId) {
      const cardValue = question.cards.find((c) => c.id === condition.cardId).text;
      infoLabel += ` - Card: ${ToPlainText(cardValue)}`;
    }

    if (condition.optionId) {
      const optionValue = this.getOptionValue(question, condition.optionId);

      if (isRankOperator) {
        conditionPreview.optionId = this.getConditionPreviewKey(question, condition.optionId);
      }

      infoLabel += ` - Option: ${ToPlainText(optionValue)}`;
    }

    if (![...RangeOperators, ...BooleanOperators].includes(condition.operator)) {
      conditionPreview.valueToMatchLabels = new Array(conditionPreview.valueToMatch.length);

      for (const value of condition.valueToMatch) {
        const index = condition.valueToMatch.indexOf(value);

        if (isRankOperator) {
          conditionPreview.valueToMatchLabels[index] = this.getRankingConditionPreviewValue(question, value);
        } else if (question.options && question.options.length >= 1) {
          const optionToMatch = question.options.find((o) => o.id === value);
          conditionPreview.valueToMatchLabels[index] = optionToMatch
            ? `"${!!optionToMatch.text ? ToPlainText(optionToMatch.text) : optionToMatch.stimulus?.name}"`
            : `"${ToPlainText(value)}"`;
        } else {
          conditionPreview.valueToMatchLabels[index] = `"${ToPlainText(value)}"`;
        }
      }
    }

    conditionPreview.infoLabel = infoLabel;

    return conditionPreview;
  }

  private getOptionValue(question: Question, id: string): string {
    switch (id) {
      case 'OTHER':
        return 'OTHER';
      case 'NONE':
        return 'NONE';
      default:
        return question.options.find((o) => o.id === id).text;
    }
  }

  private getConditionPreviewKey(question: Question, id: string): string {
    switch (id) {
      case 'OTHER':
        return 'OTHER';
      case 'NONE':
        return 'NONE';
      default: {
        if (RankingTypes.includes(question.type)) {
          const optionText = question.options.find((opt) => opt.id === id)?.text;
          return `${ToPlainText(optionText)}`;
        }

        return `${id}`;
      }
    }
  }

  private getRankingConditionPreviewValue(question: Question, value: string): string {
    if (question.type === QuestionType.RankSort && question.ratingType === RatingType.descriptive) {
      const rankText = question.ranks.find((r) => r.id === value).text;

      return `${ToPlainText(rankText)}`;
    }

    return `${ToOrdinalNumber(+value)}`;
  }
}
